import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const marstaPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-marsta",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Märsta – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Märsta. Vi hämtar möbler, vitvaror och skräp i Valsta, Sätuna, Tingvalla, Steninge och Norrbacka. Fast pris, ofta samma dag i hela Sigtuna kommun.",
      h1: "Bortforsling Märsta",
    },

    hero: {
      heading: "Bortforsling i Märsta",
      intro:
        "Boka bortforsling i Märsta – vi hämtar möbler, vitvaror och skrymmande föremål i hela Sigtuna kommun, från lägenheterna i Valsta och Tingvalla till villorna i Ekilla, Norrbacka och Steninge. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Märsta – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Märsta",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Märsta och körs till ÅVC Voltgatan",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Ekilla, Märsta",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Valsta, Märsta",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Sätuna, Märsta",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Tingvalla, Märsta",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Steninge, Märsta",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Märsta – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Märsta är centralorten i Sigtuna kommun och en av regionens viktigaste knutpunkter – med E4:an strax utanför, pendeltåg och fjärrtåg från Märsta station och Arlanda flygplats inom några minuter. Bebyggelsen är blandad: lägenhetsområdena Valsta och Tingvalla, det centrala Sätuna kring stationen, och villakvarteren i Ekilla, Norrbacka och Steninge. Den blandningen gör bortforsling till olika utmaningar beroende på var du bor. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Märsta har en stor fördel här – kommunens egen återvinningscentral på Voltgatan ligger mitt i orten, och strax intill finns Brista avfallsanläggning. Det gör att avfallet sällan behöver köras långt. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E4:an går rakt förbi Märsta och Märstavägen binder ihop kommundelarna tar vi oss snabbt mellan områdena – från Rosersberg i söder till Sigtuna stad och Steninge i väster. Det gör att vi ofta kan vara på plats i Märsta redan samma dag du bokar. Våra medarbetare är vana vid både de större bostadsgårdarna i Valsta och de smala villagatorna i Ekilla och Norrbacka – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Märsta",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Ekilla, Norrbacka eller Steninge är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Valsta, Tingvalla eller centrala Sätuna, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning. Det sparar tid jämfört med att leta plats på gatan.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som boende i Sigtuna kommun på återvinningscentralen vid Voltgatan i Märsta.",
      additionalSections: [
        {
          heading: "Områden och kommundelar vi täcker i Märsta och Sigtuna kommun",
          content:
            "Vi utför bortforsling i hela Märsta och resten av Sigtuna kommun – från de centrala lägenhetsområdena till villakvarteren och de mindre orterna. Oavsett om du bor i en lägenhet i Valsta, en villa i Ekilla, ett radhus i Steningehöjden eller en etta nära Märsta station kan vi vara på plats, ofta redan samma dag.\n\nI centrala Märsta – Sätuna kring Stationsgatan och Södergatan, samt Tingvalla och Norrbacka – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, nära pendeltågsstationen och Märsta centrum. I Valsta, med sin mer småskaliga miljonprogramsbebyggelse, hanterar vi både höghus och radhuslängor.\n\nVi är minst lika snabba i kommunens övriga delar: villaområdena i Ekilla och Steninge ner mot Mälaren, den gamla staden Sigtuna i väster, samt Rosersberg med sina logistik- och bostadskvarter i söder. Tack vare närheten till E4:an, Märstavägen och pendeltågsstationerna i Märsta och Rosersberg planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon kommundel.",
        },
        {
          heading: "Återvinning och miljöansvar i Märsta",
          content:
            "En viktig del av vår bortforsling i Märsta är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler i området. I Sigtuna kommun har vi nära till flera anläggningar, och de vi använder mest för uppdrag i Märsta är:\n\nÅtervinningscentralen på Voltgatan i Märsta – kommunens egen ÅVC, mitt i orten, där sorterat grovavfall, farligt avfall och förpackningar tas emot. Brista avfallsanläggning – belägen strax norr om Märsta, med omlastning av flera avfallsfraktioner. Vi väljer alltid den station som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Märsta med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Märsta och Sigtuna kommun, från bostadsgårdarna i Valsta och Tingvalla till villagatorna i Ekilla och Steninge, och vet att återvinningscentralen på Voltgatan ligger nära. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och E4:an går rakt förbi Märsta. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för Märsta eller någon annan del av Sigtuna kommun, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via återvinningscentralen på Voltgatan och övriga anläggningar i kommunen. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Märsta?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för Märsta eller övriga Sigtuna kommun.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Märsta?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Märsta?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och E4:an går rakt förbi Märsta, så vi tar oss snabbt mellan områdena. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Märsta?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Märsta?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Valsta, Tingvalla och Sätuna. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage i Märsta?",
        answer:
          "Ja, en stor del av våra uppdrag i Märsta är villahämtningar i Ekilla, Norrbacka, Steninge och Steningehöjden. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Märsta försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Märsta?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. I Märsta är det oftast återvinningscentralen på Voltgatan eller Brista avfallsanläggning. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Kan jag boka bortforsling i Märsta på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Märsta?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både villaförråden i Ekilla och lägenhetsförråden i Valsta och Tingvalla.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Märsta?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i Sätuna och ditt förråd på en annan adress i kommunen.",
      },
      {
        question: "Vilka områden i Märsta och Sigtuna kommun täcker ni?",
        answer:
          "Vi utför bortforsling i hela Märsta – Valsta, Sätuna, Tingvalla, Norrbacka, Ekilla, Steninge och Steningehöjden – samt i övriga Sigtuna kommun som Sigtuna stad och Rosersberg. Inga extra avgifter för någon kommundel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Märsta?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som boende i Sigtuna kommun på återvinningscentralen vid Voltgatan i Märsta.",
      },
      {
        question: "Vilka kommuner runt Märsta utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Märsta i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Märsta kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Märsta idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Märsta och Sigtuna kommun.",
    },

    en: {
      slug: "junk-removal-marsta",
      meta: {
        title: "Junk Removal Märsta – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Märsta. We pick up furniture, appliances and bulky items in Valsta, Sätuna, Tingvalla, Steninge and Norrbacka. Fixed price, often same day in Sigtuna municipality.",
        h1: "Junk Removal Märsta",
      },
      hero: {
        heading: "Junk Removal in Märsta",
        intro:
          "Book junk removal in Märsta – we collect furniture, appliances and bulky items across the whole of Sigtuna municipality, from the apartments in Valsta and Tingvalla to the villas in Ekilla, Norrbacka and Steninge. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Märsta – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Märsta",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Märsta and driven to the Voltgatan recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Ekilla, Märsta",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Valsta, Märsta",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Sätuna, Märsta",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Tingvalla, Märsta",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Steninge, Märsta",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Märsta – fast, hassle-free and responsible",
        mainText:
          "Märsta is the main town of Sigtuna municipality and one of the region's key transport hubs – with the E4 motorway just outside, commuter and long-distance trains from Märsta station, and Arlanda Airport only minutes away. The housing is mixed: the apartment areas of Valsta and Tingvalla, the central Sätuna district around the station, and the villa neighbourhoods of Ekilla, Norrbacka and Steninge. That mix makes junk removal a different challenge depending on where you live. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Märsta has a big advantage here – the municipality's own recycling centre on Voltgatan is right in the middle of town, and the Brista waste facility is close by. This means the waste rarely has to be driven far. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E4 runs straight past Märsta and Märstavägen ties the districts together, we move quickly between areas – from Rosersberg in the south to the old town of Sigtuna and Steninge in the west. This means we can often be on site in Märsta the same day you book. Our workers are used to both the larger residential courtyards of Valsta and the narrow villa streets of Ekilla and Norrbacka – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Märsta",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Ekilla, Norrbacka or Steninge, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Valsta, Tingvalla or central Sätuna, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use. It saves time compared to searching for a spot on the street.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a resident of Sigtuna municipality you can drop those off for free at the recycling centre on Voltgatan in Märsta.",
        additionalSections: [
          {
            heading: "Areas and districts we cover in Märsta and Sigtuna municipality",
            content:
              "We perform junk removal across the whole of Märsta and the rest of Sigtuna municipality – from the central apartment areas to the villa neighbourhoods and the smaller towns. Whether you live in an apartment in Valsta, a villa in Ekilla, a townhouse in Steningehöjden or a studio near Märsta station, we can be on site, often the same day.\n\nIn central Märsta – Sätuna around Stationsgatan and Södergatan, plus Tingvalla and Norrbacka – we are highly experienced with apartment pickups via elevator and stairwell, close to the commuter-rail station and Märsta centrum. In Valsta, with its smaller-scale 'miljonprogram' housing, we handle both high-rises and townhouse rows.\n\nWe are just as quick in the other parts of the municipality: the villa areas of Ekilla and Steninge down towards Lake Mälaren, the old town of Sigtuna to the west, and Rosersberg with its logistics and residential quarters to the south. Thanks to the proximity of the E4, Märstavägen and the commuter-rail stations in Märsta and Rosersberg, we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Märsta",
            content:
              "An important part of our junk removal in Märsta is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres in the area. In Sigtuna municipality we are close to several facilities, and the ones we use most for jobs in Märsta are:\n\nThe recycling centre on Voltgatan in Märsta – the municipality's own ÅVC, right in the centre of town, which receives sorted bulky waste, hazardous waste and packaging. Brista waste facility – located just north of Märsta, with transhipment of several waste fractions. We always choose the station nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Märsta with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Märsta and Sigtuna municipality, from the residential courtyards of Valsta and Tingvalla to the villa streets of Ekilla and Steninge, and that the recycling centre on Voltgatan is close by. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and the E4 runs straight past Märsta. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for Märsta or any other part of Sigtuna municipality, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via the recycling centre on Voltgatan and the other facilities in the municipality. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Märsta cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for Märsta or the rest of Sigtuna municipality.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Märsta?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Märsta?",
          answer:
            "We have vans and staff across the Stockholm region and the E4 runs straight past Märsta, so we move quickly between areas. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Märsta?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Märsta?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Valsta, Tingvalla and Sätuna. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages in Märsta?",
          answer:
            "Yes, a large share of our jobs in Märsta are villa pickups in Ekilla, Norrbacka, Steninge and Steningehöjden. We reverse onto the driveway where possible and clear garages, basements and storage units. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Märsta insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Märsta?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. In Märsta that is usually the recycling centre on Voltgatan or the Brista waste facility. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Can I book junk removal in Märsta on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Märsta?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage units, attics and garages. We are used to both the villa storage rooms in Ekilla and the apartment storage units in Valsta and Tingvalla.",
        },
        {
          question: "Can you make multiple stops during junk removal in Märsta?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in Sätuna and a storage unit at another address in the municipality, for example.",
        },
        {
          question: "Which areas in Märsta and Sigtuna municipality do you cover?",
          answer:
            "We perform junk removal across all of Märsta – Valsta, Sätuna, Tingvalla, Norrbacka, Ekilla, Steninge and Steningehöjden – as well as the rest of Sigtuna municipality such as Sigtuna town and Rosersberg. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Märsta?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a resident of Sigtuna municipality you can drop those off for free at the recycling centre on Voltgatan in Märsta.",
        },
        {
          question: "Which municipalities around Märsta do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Märsta in 3 easy steps",
        step2Text:
          "Our young workers in Märsta come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Märsta today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across all of Märsta and Sigtuna municipality.",
      },
    },
  },
];
